import { action, observable, computed } from 'mobx';

import { Model, App, ArrayApp, ModelRegistry, AppRegistry, Input } from 'core';
import { PermissionedModel } from 'apps/permissions/app';

import { OneColumnLayout } from 'apps/layout';
import { UpdateSharedFileMutation } from 'apps/share/gql';

import { FormStore } from 'libs/forms/app';
import { getExtensions } from 'libs/rendering/utils';

const revisionFormConfig = {
  addRevisionFile: {
    fields: ['revisionFile'],
  },
  fields: {
    revisionFile: {
      component: 'forms.FileField',
      optional: false,
      accept: getExtensions('GltfViewer').map(extension => `.${extension}`),
    },
  },
};

export class SharedFileRevisionModel extends Model {
  static registryName = 'SharedFileRevisionModel';
  static ModelProps = {
    file: 'FileModel',
    creator: 'UserModel',
  };

  @observable file = null;
  @observable creator = null;
  @observable version = '';
  @observable title = '';
  @observable dateCreated = '';
  @observable lastUpdated = '';
}
ModelRegistry.add(SharedFileRevisionModel);

export class SharedFileRevisionApp extends App {
  static registryName = 'SharedFileRevisionApp';
  static AppProps = {
    file: 'FileApp',
    creator: 'UserApp',
  };

  static Model = SharedFileRevisionModel;
}
AppRegistry.add(SharedFileRevisionApp);

export class SharedFileModel extends PermissionedModel {
  static registryName = 'SharedFileModel';
  static ModelProps = {
    revision: 'SharedFileRevisionModel',
    space: 'SpaceModel',
    inSpace: 'SpaceModel',
    groups: ['GroupModel'],
    firstFile: 'FileModel',
    creator: 'UserModel',
  };

  @observable revision = null;
  @observable revisions = null;
  @observable context = null;
  @observable space = null;
  @observable inSpace = null;
  @observable creator = null;
  @observable slug = '';
  @observable name = '';
  @observable groups = [];
  @observable firstFile = null;

  @computed get revisionCount() {
    return this.revisions?.totalCount || 0;
  }

  @computed get route() {
    const spaceRoute = this.space?.route;
    if (!spaceRoute.name) return {};
    return {
      name: `${spaceRoute.name}.shared.file`,
      params: { ...spaceRoute.params, sharedFileId: this.id },
    };
  }

  @computed get imageFallbackPath() {
    const projectNo = parseInt(atob(this.id).split(':')[1], 10);
    if (projectNo % 3 === 0) {
      return 'assets/avatars/fallback/project-fb.svg';
    }
    if (projectNo % 3 === 1) {
      return 'assets/avatars/fallback/project-fb2.svg';
    }
    if (projectNo % 3 === 2) {
      return 'assets/avatars/fallback/project-fb3.svg';
    }
    return 'assets/avatars/fallback/project-fb.svg';
  }
}
ModelRegistry.add(SharedFileModel);

export class SharedFileApp extends App {
  static registryName = 'SharedFileApp';
  static AppProps = {
    revision: 'SharedFileRevisionApp',
    revisions: 'SharedFileRevisionArrayApp',
    space: 'SpaceApp',
    inSpace: 'SpaceApp',
    participants: 'UserArrayApp',
    pendingInvites: 'UserArrayApp',
    groups: 'GroupsApp',
    firstFile: 'FileApp',
  };

  @observable revisionFile = null;
  @observable revisionForm = null;

  @computed get participantCount() {
    return this.groups?.participants?.length || 0;
  }

  @computed get revisionUploadProgress() {
    const fieldStore = this.revisionForm?.fieldStores.get('revisionFile');
    return fieldStore?.fileAppManager?.uploadStatusText || '';
  }

  @action initRevisionForm = () => {
    if (this.revisionForm) return;
    this.revisionForm = new FormStore(
      this,
      revisionFormConfig,
      'addRevisionFile',
    );
  };

  @action onAddSharedFileRevisionSuccess = () => {
    this.revisionForm.SetPosting(false);
    this.manager.OnRevisionAdded();
  };

  @action onAddSharedFileRevisionError = error => {
    this.revisionForm?.SetPosting(false);
    console.error(error);
  };

  @action onUploadSuccess = () => {
    // Force shared page into loading
    this.manager.sharedFile = null;
    const fieldStore = this.revisionForm.fieldStores.get('revisionFile');
    const fileId = fieldStore.fileAppManager.file?.id;
    const input = new Input({
      id: this.id,
      fileId,
    });
    const options = {
      onSuccess: this.onAddSharedFileRevisionSuccess,
      onUserErr: this.onAddSharedFileRevisionError,
      onSysErr: this.onAddSharedFileRevisionError,
    };
    UpdateSharedFileMutation.New(input, options);
  };

  @action HandleFileDrop = accepted => {
    const fieldStore = this.revisionForm.fieldStores.get('revisionFile');
    this.revisionForm.SetPosting(true);
    // FIXME - Quick approach to ensure FILE is sent with private = true.
    const acceptedPrivate = accepted;
    acceptedPrivate[0].fileObject.private = true;
    fieldStore.Drop(acceptedPrivate, [], true, this.onUploadSuccess);
    this.manager.OnRevisionDropped();
  };

  static Model = SharedFileModel;
}
AppRegistry.add(SharedFileApp);

export class SharedFileArrayApp extends ArrayApp {
  static registryName = 'SharedFileArrayApp';
  static ElementApp = SharedFileApp;
  cardLayout = new OneColumnLayout();
}
AppRegistry.add(SharedFileArrayApp);

class RevisionListLayout {
  @computed get layoutSpec() {
    return { cardColumns: 1, initialRows: 10, extraRows: 10 };
  }

  @computed get initialBatchSize() {
    return this.layoutSpec.initialRows * this.layoutSpec.cardColumns;
  }

  @computed get batchSize() {
    return this.layoutSpec.extraRows * this.layoutSpec.cardColumns;
  }
}
export class SharedFileRevisionArrayApp extends ArrayApp {
  static registryName = 'SharedFileRevisionArrayApp';
  static ElementApp = SharedFileRevisionApp;
  cardLayout = new RevisionListLayout();
}
AppRegistry.add(SharedFileRevisionArrayApp);
